import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { GetEnquiryDetail } from "../../api/enquiryService";
import { format, parseISO } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import PriceValue from "../../components/Common/PriceValue";
import { useAuth } from "../../context/AuthContext";
import "../../css/VaultAccess/enquiry_detail.css";
import { Skeleton } from 'primereact/skeleton';
//import FullScreenLoader from "../../components/Common/Loader";

const DEFAULT_IMAGE = "/assets/images/VaultAccess/no-image3.png";

const EnquiryDetail = () => {
    document.title = "Enquiry Detail | Meera Jewellery House Admin";

    const { user } = useAuth();
    const [showLoader, setShowLoader] = useState(false);
    const { ENQUIRY_ID } = useParams();
	const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const [dataReady, setDataReady] = useState(false);
	const [enquiryItems, setEnquiryItems] = useState([]);
    const [loadedImages, setLoadedImages] = useState({});
    const [errorMessage, setErrorMessage] = useState(""); 
	const isInvalid = (val) => val === null || val === undefined || val === "" || Number(val) === 0;
	const formatPcsCts = (pcs, wt) => {
		return isInvalid(pcs) || isInvalid(wt) ? "-" : `${pcs} Pcs / ${wt} Cts`;
	};

	const formatDateKeepTime = (dateTime) => {
		if (!dateTime) return "-";

		const date = parseISO(dateTime);
		return format(date, "dd MMM yyyy hh:mm a");
	};

	const showVal = (val) => val === 0 || val === "0" || val === null || val === undefined ? "-" : val;

	useEffect(() => {
		fetchEnquiryDetail();
    }, [ENQUIRY_ID]);

    const fetchEnquiryDetail = async () => {
        if (!ENQUIRY_ID) return;

        setShowLoader(true);
        setDataReady(false);

        try {
            const payload = {
                Enquiry_ID: ENQUIRY_ID,
                Party_ID: user?.SelectedCustomer_ID || "0",
                OrderCategory: "ENQUIRY",
            };

            const response = await GetEnquiryDetail(payload);

            const messageObj = response?.Message?.[0] || response?.ZMESSAGE?.[0];
            const returnCode = messageObj?.RETURNCODE;
            const message = messageObj?.RETURNMESSAGE;
            const data = response?.EnquiryDetail || response?.ROW || [];

            if (returnCode === "1") {
                setEnquiryItems(data);

                if (data.length === 0) {
                    setErrorMessage("No enquiry items found.");
                } else {
                    setErrorMessage("");
                }
            } else {
                setEnquiryItems([]);
                setErrorMessage(message || "Something went wrong.");
            }
        } catch (error) {
            console.error("Error fetching enquiry detail:", error);
            setEnquiryItems([]);
            setErrorMessage("Failed to fetch enquiry details.");
        } finally {
            setProgress(100);
            setTimeout(() => {
                setShowLoader(false);
                setDataReady(true);
            }, 400);
        }
    };

    useEffect(() => {
        if (!showLoader) return;
        let value = 10;
        setProgress(value);
        const interval = setInterval(() => {
            value = Math.min(value + Math.random() * 15, 90);
            setProgress(value);
        }, 400);
        return () => clearInterval(interval);
    }, [showLoader]);

    const getStatusClass = (status = "") => status.toLowerCase().replace(/[^a-z0-9]+/g, "-");

	const handleImageLoad = (id) => {
		setLoadedImages(prev => ({ ...prev, [id]: true }));
	};

	const listVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.08,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 18 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.45,
				ease: "easeOut",
			},
		},
		exit: {
			opacity: 0,
			y: -12,
			transition: { duration: 0.25 },
		},
	};

	return (
        <div className="enquiry-detail-page">
            <div className="enquiry-header-card">
                <div className="enquiry-detail-header enquiry-header-top-row">
                    <h1 className="enquiry-page-title">
                        Enquiry Detail <span>({enquiryItems.length} items)</span>
                    </h1>
                    <div className="enquiry-header-actions-right">
                        <button className="back-btn" onClick={() => navigate(-1)} aria-label="Go back">
                            <i className="pi pi-arrow-left"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div className="enquiry-layout">
                <div className="cart-main-section">
                    <div className="cart-items-list-exact">
                        {!dataReady ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <div className="enquiry-item" key={i} style={{ opacity: 0.7 }}>
                                    <div className="item-left-visual">
                                        <Skeleton width="180px" height="180px" borderRadius="12px" />
                                    </div>
                                    <div className="item-details-container">
                                        <div className="item-info-col">
                                            <Skeleton width="90%" height="1.8rem" style={{ marginBottom: '1.2rem' }} />
                                            <div className="item-ref-code"><Skeleton width="60%" height="0.9rem" /></div>
                                            <div className="item-ref-code" style={{ marginTop: '0.8rem' }}><Skeleton width="55%" height="0.9rem" /></div>
                                            <div className="item-clock-date" style={{ marginTop: '1.5rem' }}>
                                                <Skeleton width="140px" height="0.8rem" />
                                            </div>
                                        </div>
                                        <div className="spec-column">
                                            <div className="spec-title-exact"><Skeleton width="80%" height="1.1rem" /></div>
                                            <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                                            <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                                            <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                                        </div>
                                        <div className="spec-column">
                                            <div className="spec-title-exact"><Skeleton width="80%" height="1.1rem" /></div>
                                            <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                                            <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                                            <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                                        </div>
                                        <div className="item-actions-col">
                                            <div className="item-price-exact"><Skeleton width="120px" height="2rem" /></div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) :
                        enquiryItems.length === 0 ? (
                            <div className="enquiry-empty-wrapper py-5">
                                <div className="enquiry-empty-state text-center">
                                    <img
                                        src="/assets/images/enquiry/noEnquiry.png"
                                        alt="Error"
                                        className="enquiry-empty-image mb-3"
                                        style={{ maxWidth: "150px" }}
                                    />
                                    <h5 className="fw-semibold mb-2">NO ENQUIRY DETAIL</h5>
                                    <p className="text-muted mb-3">{errorMessage}</p>
                                </div>
                            </div>
                        ) : null}

                        <AnimatePresence>
                            {dataReady && enquiryItems.map((item, index) => (
                                <motion.div
                                    key={item.Style_ID || index}
                                    layout
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.98 }}
                                    transition={{ duration: 0.4, delay: index * 0.08 }}
                                    className="enquiry-item"
                                >
                                    <div className="item-left-visual">
                                        <Link to={`/product-detail/${item.Style_ID}`} style={{ display: 'block', height: '100%' }}>
                                            <img
                                                src={item.Image1 || DEFAULT_IMAGE}
                                                alt={item.StyleNo}
                                                onError={(e) => {
                                                    e.target.src = DEFAULT_IMAGE;
                                                }}
                                            />
                                        </Link>
                                    </div>

                                    <div className="item-details-container">
                                        {/* Column 1: Info */}
                                        <div className="item-info-col">
                                            <h2 className="item-title-exact">
                                                {item.StyleNo} - {item.Shortdescription || "Live Inventory"}
                                            </h2>
                                            <div className="item-ref-code">
                                                Jewel Code: <span>{item.JewelCode}</span>
                                            </div>
                                            <div className="item-ref-code">
                                                Style No: <span>{item.StyleNo}</span>
                                            </div>
                                            <div className="item-clock-date">
                                                <i className="pi pi-clock" style={{ fontSize: '0.9rem' }} />
                                                {formatDateKeepTime(item.EnquiryDate)}
                                            </div>
                                        </div>

                                        {/* Column 2: Product Details */}
                                        <div className="spec-column">
                                            <div className="spec-title-exact">Product Details</div>
                                            <div className="spec-row-exact">
                                                <span className="spec-label-exact">Purity</span>
                                                <span className="spec-value-exact">{item.MetalQuality || "-"}</span>
                                            </div>
                                            <div className="spec-row-exact">
                                                <span className="spec-label-exact">Gold Wt</span>
                                                <span className="spec-value-exact">{item.TotalNetWt ? `${item.TotalNetWt} g` : "-"}</span>
                                            </div>
                                            <div className="spec-row-exact">
                                                <span className="spec-label-exact">Dimension</span>
                                                <span className="spec-value-exact">{item.ItemSize || "-"}</span>
                                            </div>
                                        </div>

                                        {/* Column 3: Diamond Details */}
                                        <div className="spec-column">
                                            <div className="spec-title-exact">Diamond Details</div>
                                            <div className="spec-row-exact">
                                                <span className="spec-label-exact">Weight</span>
                                                <span className="spec-value-exact">
                                                    {formatPcsCts(item.DiaPcs, item.DiaWt)}
                                                </span>
                                            </div>
                                            <div className="spec-row-exact">
                                                <span className="spec-label-exact">Gross Wt</span>
                                                <span className="spec-value-exact">{isInvalid(item.GrossWt) ? "-" : `${item.GrossWt} g`}</span>
                                            </div>
                                            <div className="spec-row-exact">
                                                <span className="spec-label-exact">Color</span>
                                                <span className="spec-value-exact">{item.MetalTone || "-"}</span>
                                            </div>
                                        </div>

                                        {/* Column 4: Price */}
                                        <div className="item-actions-col">
                                            <div className="item-price-exact fs-4">
                                                {item.LineAmount}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
		</div>
	);
};

export default EnquiryDetail;
